import React, { useState } from 'react';
import {
  PermissionSchemesForm,
  StyledInput,
  AddPermissionSchemeButton,
  PermissionSchemesFormTitle,
  PermissionSchemeInputContainer,
  Card,
} from './styles';
import { IOption } from 'components/Select/Select';
import Select from 'components/Select';
import { DeleteIcon } from 'components/SurveyResponses/styles';
import { IProviders } from 'store/store';

import {
  ICompany,
  ICompanyPermissionScheme,
  ICompanyRole,
} from 'store/companies/companiesTypes';
import { ITagView } from 'store/providers/providerTypes';
import i18n, { languageDirection } from '../../../../i18n';

interface IPermissionSchemeInputProps {
  addOrUpdatePermissionScheme: (
    permissionScheme: ICompanyPermissionScheme
  ) => Promise<any>;
  loading: boolean;
  companyId: number;
  company: ICompany;
  cancelEdit: () => void;
  permissionScheme?: ICompanyPermissionScheme;
  allTags: IProviders['tags'];
  companyProvidersIdName: [number, string][];
}

const PermissionSchemeInput: React.FC<IPermissionSchemeInputProps> = ({
  addOrUpdatePermissionScheme,
  loading,
  companyId,
  permissionScheme,
  cancelEdit,
  allTags,
  companyProvidersIdName,
  company,
}) => {
  const getTagProviderMap = (allTags: IProviders['tags']) => {
    const ret = {};
    Object.entries(allTags || {}).forEach(([providerId, itags]) => {
      Object.entries(itags?.data || {})?.forEach(([id, _]) => {
        ret[id] = providerId;
      });
    });
    return ret;
  };
  const tagProviderMap = getTagProviderMap(allTags);

  const newPSScheme = {
    companyId: companyId,
    id: null,
    properties: { description: '', name: '', roleIdTagIdListMap: {} },
  };
  const [ps, setPS] = useState(permissionScheme || newPSScheme);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    addOrUpdatePermissionScheme(ps as ICompanyPermissionScheme).then(() => {
      setPS(newPSScheme);
    });
  };

  const onSelectRole = (rolesOptions: IOption[]) => {
    const newPs = JSON.parse(JSON.stringify(ps));
    newPs.properties.roleIdTagIdListMap[Number(rolesOptions[0].value)] = [];
    setPS(newPs);
  };
  const onRemoveRole = (roleId: string) => {
    const newPs = JSON.parse(JSON.stringify(ps));
    delete newPs.properties.roleIdTagIdListMap[roleId];
    setPS(newPs);
  };
  const onSetTagToRole =
    (roleId: number, providerId: number) => (selected: IOption[]) => {
      const newPs = JSON.parse(JSON.stringify(ps));
      let newValue = newPs.properties.roleIdTagIdListMap[roleId].reduce(
        (current: number[], item: number) => {
          if (Number(tagProviderMap[item]) !== Number(providerId))
            current.push(item);
          return current || [];
        },
        [] as number[]
      );
      newValue = [...newValue, ...selected.map((it) => Number(it.value))];
      newPs.properties.roleIdTagIdListMap[roleId] = newValue;
      setPS(newPs);
    };

  const companyRoles = Object.values(company.roles).reduce(
    (usersList: any, role: ICompanyRole | undefined) => {
      usersList.push({
        label: role!.name!,
        value: role!.id!,
      });
      return usersList;
    },
    [] as IOption[]
  );
  return (
    <PermissionSchemesForm onSubmit={onSubmit}>
      <PermissionSchemesFormTitle>
        {permissionScheme && permissionScheme.id
          ? i18n.t('editPermissionScheme', {
              ns: 'companyManagement',
            })
          : ''}
      </PermissionSchemesFormTitle>
      <PermissionSchemeInputContainer>
        <StyledInput
          id="permissionSchemeName"
          type="text"
          required={true}
          placeholder={i18n.t('permissionSchemeHint', {
            ns: 'companyManagement',
          })}
          autoComplete="off"
          value={ps?.properties.name}
          onChange={(text: any) => {
            const newPs = JSON.parse(JSON.stringify(ps));
            newPs.properties.name = text.target.value;
            setPS(newPs);
          }}
        />
        <StyledInput
          id="permissionSchemeDescription"
          type="text"
          placeholder={i18n.t('permissionSchemeDescription', {
            ns: 'companyManagement',
          })}
          autoComplete="off"
          value={ps?.properties.description}
          onChange={(text: any) => {
            const newPs = JSON.parse(JSON.stringify(ps));
            newPs.properties.description = text.target.value;
            setPS(newPs);
          }}
        />
        {ps?.id && (
          <Select
            onChangeValues={(values) => onSelectRole(values)}
            values={undefined}
            label={
              i18n.t('addRole', {
                ns: 'companyManagement',
              }) as string
            }
            options={companyRoles.filter((it: IOption) => {
              return !(
                it.value in
                (permissionScheme?.properties.roleIdTagIdListMap || {})
              );
            })}
            placeholder={
              i18n.t('addRole', {
                ns: 'companyManagement',
              }) as string
            }
          />
        )}
        <div>
          {Object.entries(ps?.properties.roleIdTagIdListMap || {}).map(
            ([roleId, tagIds]: [string, Array<number>]) => {
              return (
                <Card key={roleId} style={{ position: 'relative' }}>
                  <div
                    style={{
                      minHeight: '20px',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      width: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    <b>{company.roles[Number(roleId)]?.name}</b>
                    <DeleteIcon
                      style={{
                        position: 'relative',
                        display: 'flex',
                      }}
                      type={'x'}
                      onClick={() => {
                        onRemoveRole(roleId);
                      }}
                    />
                  </div>
                  <div>
                    {companyProvidersIdName.map(
                      ([providerId, name], _, list) => {
                        return (
                          <div key={providerId}>
                            {list.length > 1 && <span>{name}</span>}
                            <Select
                              disabled={permissionScheme?.autoGenerated}
                              onChangeValues={onSetTagToRole(
                                Number(roleId),
                                providerId
                              )}
                              values={tagIds
                                ?.filter(
                                  (it) =>
                                    Number(tagProviderMap[it]) ===
                                    Number(providerId)
                                )
                                .map((it) => {
                                  return {
                                    value: it,
                                    label:
                                      allTags[providerId]!.data[it].text +
                                      (allTags[providerId]!.data[it].deleted
                                        ? i18n.t('deleted', {
                                            ns: 'companyManagement',
                                          })
                                        : ''),
                                  };
                                })}
                              multiChoice={true}
                              placeholder={i18n.t('availableChoices', {
                                ns: 'companyManagement',
                              })}
                              options={
                                Object.values(
                                  allTags[providerId]?.data || {}
                                )?.reduce(
                                  (tagsList: IOption[], tag: ITagView) => {
                                    if (tag.deleted) return tagsList;
                                    const option: IOption = {
                                      label:
                                        tag.text +
                                        (tag.deleted
                                          ? i18n.t('deleted', {
                                              ns: 'companyManagement',
                                            })
                                          : ''),
                                      value: tag.id,
                                    };
                                    tagsList.push(option);
                                    return tagsList;
                                  },
                                  []
                                ) ?? []
                              }
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                </Card>
              );
            }
          )}
        </div>
        <div>
          {(!permissionScheme?.autoGenerated || !permissionScheme.id) && (
            <AddPermissionSchemeButton
              color="green"
              type="submit"
              loading={loading}
            >
              {permissionScheme && permissionScheme.id
                ? i18n.t('save', {
                    ns: 'companyManagement',
                  })
                : i18n.t('addPermissionScheme', {
                    ns: 'companyManagement',
                  })}
            </AddPermissionSchemeButton>
          )}
          {permissionScheme && permissionScheme.id && (
            <AddPermissionSchemeButton
              color="red"
              onClick={cancelEdit}
              loading={loading}
              style={{
                float: languageDirection() === 'rtl' ? 'left' : 'right',
              }}
            >
              {i18n.t('cancel', {
                ns: 'companyManagement',
              })}
            </AddPermissionSchemeButton>
          )}
        </div>
      </PermissionSchemeInputContainer>
    </PermissionSchemesForm>
  );
};

export default PermissionSchemeInput;
